import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Upload, Megaphone, FileText } from "lucide-react";

const Ok = ({ ok }) => ok
  ? <CheckCircle2 size={16} className="text-emerald-600 inline" />
  : <XCircle size={16} className="text-red-500 inline" />;

export default function Condicoes() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [ativ, setAtiv] = useState(null);
  const [nome, setNome] = useState("");
  const [versao, setVersao] = useState("1.0");
  const [arquivo, setArquivo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/empresa/condicoes-status").then(r => setSt(r.data)).catch(e => toast.error(formatApiError(e)));
    api.get("/empresa/ativacao-link").then(r => setAtiv(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const enviarContrato = async () => {
    if (!arquivo || !nome.trim()) { toast.error("Informe o nome do documento e o arquivo (PDF/DOCX)"); return; }
    const fd = new FormData();
    fd.append("nome", nome); fd.append("versao", versao); fd.append("arquivo", arquivo);
    setBusy(true);
    try {
      await api.post("/empresa/contrato-proprio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contrato próprio anexado — será enviado junto com as condições");
      setNome(""); setArquivo(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const publicar = async () => {
    if (!window.confirm("Publicar as condições para a rede? Montadoras e Montadores elegíveis da sua região receberão tudo para aceite.")) return;
    setBusy(true);
    try {
      const r = await api.post("/empresa/publicar-condicoes", {});
      toast.success(`Condições v${r.data.versao} publicadas · ${r.data.montadoras} montadora(s) e ${r.data.montadores} montador(es) notificados`);
      toast.info("Próximo passo: importe sua primeira planilha de serviços", {
        duration: 10000,
        action: { label: "Importar agora", onClick: () => navigate("/admin/importar") },
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!st) return <div className="p-8 text-sm text-muted-foreground">Carregando…</div>;
  const cal = st.calendario || {};
  const fiscal = st.fiscal || {};
  return (
    <div className="p-8 max-w-4xl" data-testid="condicoes-page">
      <div className="label-cap">Comercial · Empresa</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Publicar Condições para a Rede</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Sua empresa <strong>não escolhe</strong> montadoras nem montadores: ao publicar, o MontaJá localiza
        automaticamente os elegíveis da sua região e envia as condições para aceite.
      </p>

      <div className="border border-border bg-white p-5 mt-6" data-testid="condicoes-checklist">
        <div className="label-cap mb-3">Checklist obrigatório</div>
        <div className="space-y-2 text-sm">
          <div><Ok ok={st.contrato_montaja_ok} /> Contrato Empresa × MontaJá assinado
            {!st.contrato_montaja_ok && ativ?.link && (
              <a href={ativ.link} target="_blank" rel="noreferrer"
                className="ml-2 text-xs underline text-[#FF6700] font-bold" data-testid="link-ativacao">
                Concluir ativação →</a>
            )}
          </div>
          <div><Ok ok={st.documentacao_status === "aprovada"} /> Documentação conferida pelo MontaJá
            {st.documentacao_status === "em_conferencia" && <span className="text-xs text-amber-600 ml-1">— em conferência (você pode continuar configurando)</span>}
            {st.documentacao_status === "correcao_necessaria" && <span className="text-xs text-red-600 ml-1">— ajuste solicitado: {ativ?.documentacao_motivo}</span>}
          </div>
          <div><Ok ok={st.tabela_ok} /> Tabela de Valores {st.tabela && <span className="text-xs text-muted-foreground">— {st.tabela.itens?.length} serviço(s)</span>}</div>
          <div><Ok ok={st.fiscal_ok} /> Configuração Fiscal {fiscal.exige_nota_fiscal != null && <span className="text-xs text-muted-foreground">— NF: {fiscal.exige_nota_fiscal ? "SIM" : "NÃO"}</span>}</div>
          <div><Ok ok={st.calendario_ok} /> Calendário de Fechamento e Pagamento</div>
        </div>
      </div>

      <div className="border border-border bg-white p-5 mt-4" data-testid="contrato-proprio">
        <div className="label-cap mb-1"><FileText size={13} className="inline mr-1" />Contrato próprio da empresa (opcional)</div>
        <p className="text-xs text-muted-foreground mb-3">
          Enviado aos profissionais junto com a ficha padronizada MontaJá. <strong>Não substitui</strong> a
          Tabela de Valores, regras fiscais, calendário nem os registros obrigatórios do MontaJá.
        </p>
        {st.contrato?.id && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 p-2 mb-3" data-testid="contrato-atual">
            ✓ Ativo: <strong>{st.contrato.nome}</strong> v{st.contrato.versao} ({st.contrato.filename})
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-2">
          <Input placeholder="Nome do documento" value={nome} onChange={e => setNome(e.target.value)} data-testid="contrato-nome" />
          <Input placeholder="Versão (ex: 1.0)" value={versao} onChange={e => setVersao(e.target.value)} data-testid="contrato-versao" />
          <input type="file" accept=".pdf,.docx,.doc" onChange={e => setArquivo(e.target.files?.[0] || null)}
            className="text-xs" data-testid="contrato-arquivo" />
        </div>
        <Button onClick={enviarContrato} disabled={busy} variant="outline" className="btn-sharp mt-3" data-testid="contrato-enviar">
          <Upload size={14} /> {st.contrato?.id ? "Substituir contrato" : "Anexar contrato"}
        </Button>
      </div>

      <div className="border-2 border-[#142766] bg-white p-5 mt-4" data-testid="condicoes-resumo">
        <div className="label-cap mb-3">Resumo das condições</div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {(st.tabela?.itens || []).map(it => (
            <div key={it.tipo_servico} className="flex justify-between border-b border-dashed border-border py-1">
              <span className="capitalize">{it.tipo_servico.replace(/_/g, " ")}
                {it.below_network_reference && <span className="ml-1 text-[10px] text-amber-600 font-bold">▼ abaixo da referência</span>}
              </span>
              <strong>{it.modo === "percentual" ? `${it.valor_montador}%` : `R$ ${Number(it.valor_montador).toFixed(2)}`}</strong>
            </div>
          ))}
          <div className="flex justify-between py-1"><span>Exige NF</span><strong>{fiscal.exige_nota_fiscal ? "SIM" : "NÃO"}</strong></div>
          <div className="flex justify-between py-1"><span>CNPJ ativo</span><strong>{fiscal.aceita_montador === "apenas_cnpj_ativo" ? "Obrigatório" : "Não obrigatório"}</strong></div>
          <div className="flex justify-between py-1"><span>Fechamento</span><strong>{cal.periodicidade || "—"}</strong></div>
          <div className="flex justify-between py-1"><span>Contrato próprio</span><strong>{st.contrato?.id ? `Sim — v${st.contrato.versao}` : "Não"}</strong></div>
        </div>
        <Button onClick={publicar} disabled={busy || !st.pronto_para_publicar}
          className="btn-sharp w-full mt-5 h-12 bg-[#FF6700] hover:bg-[#e05a00] text-white text-base font-bold"
          data-testid="publicar-condicoes">
          <Megaphone size={17} /> PUBLICAR CONDIÇÕES PARA A REDE
        </Button>
        {!st.pronto_para_publicar && <p className="text-[11px] text-red-600 mt-2 text-center">Complete o checklist acima para liberar a publicação.</p>}
      </div>

      {st.publicada && (
        <div className="border border-border bg-white p-5 mt-4" data-testid="condicoes-rede">
          <div className="label-cap mb-2">Rede · condições v{st.publicada.versao} (publicadas em {new Date(st.publicada.publicado_em).toLocaleDateString("pt-BR")})</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center text-sm">
            {["montadora", "montador"].map(t => (
              <React.Fragment key={t}>
                <div className="border border-border p-2"><div className="text-xl font-bold text-emerald-600">{st.rede[`${t}_aceito`] || 0}</div><div className="text-[10px] uppercase">{t}s aceitaram</div></div>
                <div className="border border-border p-2"><div className="text-xl font-bold text-amber-600">{st.rede[`${t}_pendente`] || 0}</div><div className="text-[10px] uppercase">{t}s pendentes</div></div>
                <div className="border border-border p-2"><div className="text-xl font-bold text-red-500">{st.rede[`${t}_recusado`] || 0}</div><div className="text-[10px] uppercase">{t}s recusaram</div></div>
              </React.Fragment>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Transição inteligente: quem aceitou tem prioridade nas OS; se ninguém da rede aceita atender,
            a OS vai aos demais elegíveis da região.
          </p>
          <Button onClick={() => navigate("/admin/importar")} variant="outline"
            className="btn-sharp w-full mt-3 h-11 border-[#142766] text-[#142766] font-bold"
            data-testid="importar-primeira-planilha">
            <FileText size={15} /> IMPORTAR MINHA PRIMEIRA PLANILHA DE SERVIÇOS →
          </Button>
        </div>
      )}
    </div>
  );
}
